"use strict";
import { addBreadcrumb } from "@sentry/browser";
import _ from "lodash";
import { CLOUDINARY_LIB_URL, SID_LOOKUP } from "./app.constants";
import { growerSetupState } from "./grower.routes";
import { AUTHENTICATED_PERMISSION } from "./common/auth/auth.constants";
import {
    getCloudinaryUrl,
    getLatestMembership,
    lazyLoad,
} from "./common/common/common.utils";
import {
    EXTERNAL,
    COMPLETE as PAPERWORK_COMPLETE,
    PENDING as PAPERWORK_PENDING,
    SIGNER,
} from "./common/paperwork/constants";
import {
    filterOutBadEnums,
    getNextRole,
    getSignatureFieldNames,
} from "./common/paperwork/paperwork.utils";
import {
    CANCELLED,
    COMPLETE,
    PENDING,
    PENDING as SAFETY_PENDING,
} from "./common/safety/safety.constants";
import { REMOTE } from "./common/safety/constants";
import cookiesRequiredTemplate from "./cookies_required.html";

export const homescreenState = {
    name: "app.homescreen",
    url: "/home",
    component: "homeScreen",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "common.home" */ "./common/home"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        growerMemberships: function (GrowerMembershipService, accountSid) {
            "ngInject";
            return GrowerMembershipService.list({
                user__sid: accountSid,
                active: true,
            });
        },
        growers: function (GrowerOrganizationService, growerMemberships) {
            "ngInject";
            return GrowerOrganizationService.several(
                _.map(growerMemberships, "grower_organization"),
            );
        },
        growerManagementMemberships: function (
            GrowerManagementMembershipService,
            accountSid,
        ) {
            "ngInject";
            return GrowerManagementMembershipService.list({
                active: true,
                user__sid: accountSid,
            });
        },
        managedGrowers: function (
            GrowerOrganizationService,
            growerManagementMemberships,
        ) {
            "ngInject";
            return GrowerOrganizationService.several(
                _.map(growerManagementMemberships, "grower_organization"),
            );
        },
        managedCrews: function (GroupService, accountSid) {
            "ngInject";
            return GroupService.list({ managed_by: accountSid });
        },
        managedCrewsData: function (managedCrews, growers) {
            "ngInject";
            return _.map(managedCrews, function (crew) {
                let grower = _.find(growers, [
                    SID_LOOKUP,
                    crew.grower_organization,
                ]);
                return {
                    crew: crew,
                    grower_organization: grower,
                };
            });
        },
        myIncompleteDocumentRequests: function (
            DocumentPackageRequestService,
            accountSid,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                signer__sid: accountSid,
                completed: false,
                cancelled: false,
            });
        },
        mySignatureRequests: function (
            SignatureRequestService,
            myIncompleteDocumentRequests,
            accountSid,
        ) {
            "ngInject";
            return SignatureRequestService.list({
                signer__sid: accountSid,
                role: SIGNER,
                document_package_request__sid__in: _.join(
                    _.map(myIncompleteDocumentRequests, SID_LOOKUP),
                ),
            });
        },
        documentPackages: function (
            myIncompleteDocumentRequests,
            DocumentPackageService,
        ) {
            "ngInject";
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    _.map(myIncompleteDocumentRequests, SID_LOOKUP),
                ),
            });
        },
        paperworkToDo: function (
            myIncompleteDocumentRequests,
            mySignatureRequests,
            documentPackages,
        ) {
            "ngInject";
            return _.map(
                myIncompleteDocumentRequests,
                function (documentRequest) {
                    let documentPackage = _.find(documentPackages, [
                        SID_LOOKUP,
                        documentRequest.document_package,
                    ]);
                    return {
                        documentRequest: documentRequest,
                        signatureRequest: _.find(mySignatureRequests, [
                            "document_package_request",
                            documentRequest.sid,
                        ]),
                        documentPackage,
                        nextRole: getNextRole(documentRequest, documentPackage),
                    };
                },
            );
        },
        newPaperworkWaitingForPrefill: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return paperwork.documentRequest.isInPrefill();
            });
        },
        oldPaperworkToStart: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return (
                    paperwork.nextRole === SIGNER &&
                    _.isNil(
                        paperwork.documentRequest
                            .next_pending_signature_request,
                    ) &&
                    _.isNil(paperwork.signatureRequest) &&
                    !paperwork.documentRequest.isInPrefill() &&
                    !paperwork.documentRequest.source
                );
            });
        },
        paperworkV3ToSign: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return (
                    _.isNil(
                        _.get(paperwork.signatureRequest, "data_request_id"),
                    ) &&
                    _.get(paperwork.signatureRequest, "sid") ===
                        paperwork.documentRequest.next_pending_signature_request
                );
            });
        },
        safetyMeetingRequests: function (
            SafetyMeetingRequestService,
            accountSid,
        ) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                active: true,
                subject__sid: accountSid,
                status: SAFETY_PENDING,
                safety_meeting__delivery_type: REMOTE,
            });
        },
    },
    data: {
        title: "routes.HOME_TITLE",
        mixpanelEvent: "Viewed homescreen",
    },
    redirectTo: function (trans) {
        let hijacked = trans
            .injector()
            .get("localStorageService")
            .get("hijacked");
        if (hijacked) {
            return;
        } else {
            return trans
                .injector()
                .getAsync("managedGrowers")
                .then(function (managedGrowers) {
                    for (let grower of managedGrowers) {
                        if (!grower.is_active) {
                            return trans.router.stateService.target(
                                growerSetupState,
                                { grower_sid: grower.sid },
                            );
                        }
                    }
                });
        }
    },
};

export const signinState = {
    name: "signin",
    url: "/signin?redirect_to?mobile_email",
    params: {
        redirect_to: {
            inherit: false,
            value: undefined,
        },
        mobile_email: {
            inherit: false,
            value: undefined,
        },
    },
    component: "signin",
    resolve: {
        credentials: function ($transition$) {
            "ngInject";
            return {
                mobile_email: $transition$.params().mobile_email,
                redirect_to: $transition$.params().redirect_to,
            };
        },
    },
    data: {
        permissions: {
            except: [AUTHENTICATED_PERMISSION],
            redirectTo: homescreenState.name,
        },
        title: "routes.SIGN_IN_TITLE",
        mixpanelEvent: "Viewed signin",
    },
};

export const appState = {
    name: "app",
    abstract: true,
    component: "frameComponent",
    resolve: {
        accountSid: function (AccountService, AuthService) {
            "ngInject";
            return AuthService.getAuthenticatedUserSid();
        },
        account: function (AccountService, accountSid, PermPermissionStore) {
            "ngInject";
            var permissions = PermPermissionStore.getStore();
            return AccountService.retrieve(accountSid);
        },
    },
    data: {
        permissions: {
            only: [AUTHENTICATED_PERMISSION],
            redirectTo: {
                default: signinState.name,
            },
        },
    },
};
export const errorTestState = {
    name: "errorTest",
    url: "/error",
    controller: function () {
        throw new TypeError("This is a test error");
    },
    data: {
        title: "routes.ERROR_TEST_TITLE",
        mixpanelEvent: "Viewed error test",
    },
};
export const signinDoneState = {
    name: "signin.done",
    url: "/done?channel",
    component: "signinDone",
    resolve: {
        channel: function ($transition$) {
            "ngInject";
            return $transition$.params().channel;
        },
    },
    data: {
        permissions: {
            except: [AUTHENTICATED_PERMISSION],
            redirectTo: homescreenState.name,
        },
        title: "routes.SIGN_IN_DONE_TITLE",
        mixpanelEvent: "Viewed signin done",
    },
};
export const cookiesRequiredState = {
    name: "cookies",
    url: "/cookies-required",
    template: cookiesRequiredTemplate,
    data: {
        title: "routes.COOKIES_REQUIRED_TITLE",
        mixpanelEvent: "Viewed cookies required",
    },
};
export const oauth2AuthorizeState = {
    name: "oauth2Authorize",
    url: "/oauth2/authorize?response_type?client_id?redirect_uri?scope?state",
    redirectTo: (trans) => {
        let AuthService = trans.injector().get("AuthService");
        AuthService.oauth2Authorize(trans.params());
    },
};
export const resetState = {
    name: "reset",
    url: "/reset",
    redirectTo: (trans) => {
        let AuthService = trans.injector().get("AuthService");
        let $cookies = trans.injector().get("$cookies");
        let $window = trans.injector().get("$window");
        AuthService.clearStorage();
        $cookies.remove("nf_ab");
        $window.location = "/signin";
    },
};
export const publicWaflaContractListState = {
    name: "visatrace",
    url: "/visatrace/{grower_airtable_id}",
    component: "publicContractList",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "grower.wafla" */ "./grower/wafla"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        contractsData: function (WAFLAContractService, $transition$) {
            "ngInject";
            return WAFLAContractService.retrieve(
                $transition$.params().grower_airtable_id,
            );
        },
        grower: function (contractsData) {
            "ngInject";
            return contractsData.grower;
        },
        activeContracts: function (contractsData) {
            "ngInject";
            return _.filter(contractsData.contracts, function (contract) {
                return _.includes(
                    [
                        "Active",
                        "Crossing Completed",
                        "Transfer",
                        "Processing Outbound",
                    ],
                    contract.status,
                );
            });
        },
        doneContracts: function (contractsData) {
            "ngInject";
            return _.filter(contractsData.contracts, ["status", "Done"]);
        },
    },
    data: {
        title: "routes.WAFLA_DASHBOARD",
        breadcrumb: "crumbs.WAFLA_DASHBOARD",
        mixpanelEvent: "Viewed public wafla dashboard",
    },
};
export const forbiddenState = {
    name: "app.forbidden",
    url: "/forbidden",
    params: {
        redirect: {
            value: {
                state: homescreenState.name,
                params: {},
            },
            squash: true,
        },
    },
    component: "forbidden",
    resolve: {
        redirect: function ($transition$) {
            "ngInject";
            return $transition$.params().redirect;
        },
    },
    data: {
        title: "routes.FORBIDDEN_TITLE",
        mixpanelEvent: "Viewed forbidden landing page",
    },
};
export const settingsState = {
    name: "app.settings",
    url: "/settings",
    component: "settings",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "common.account" */ "./common/account"),
        ]).then(function (modules) {
            return $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        profile: function (ProfileService, account) {
            "ngInject";
            return ProfileService.retrieve(account.profile.sid);
        },
    },
    data: {
        title: "routes.SETTINGS_TITLE",
        mixpanelEvent: "Viewed account settings",
    },
};
export const accountEditState = {
    name: "app.settings.accountEdit",
    url: "/account/edit",
    component: "accountEdit",
    data: {
        title: "routes.ACCOUNT_EDIT_TITLE",
        mixpanelEvent: "Viewed edit account",
    },
};
export const profileEditState = {
    name: "app.settings.profileEdit",
    url: "/profile/edit",
    component: "profileEdit",
    lazyLoad: function ($transition$) {
        return lazyLoad($transition$, CLOUDINARY_LIB_URL);
    },
    data: {
        title: "routes.PROFILE_EDIT_TITLE",
        mixpanelEvent: "Viewed edit profile",
    },
};
export const safetyState = {
    name: "app.safety",
    url: "/safety",
    abstract: true,
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "common.safety" */ "./common/safety"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    data: {},
};

export const growerSafetyMeetingsBaseState = {
    name: "app.safety.grower",
    url: "/{grower_sid}",
    resolve: {
        safetyMeetingRequests: function (
            SafetyMeetingRequestService,
            accountSid,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                active: true,
                subject__sid: accountSid,
                status: SAFETY_PENDING,
                safety_meeting__delivery_type: REMOTE,
                requester__sid: $transition$.params().grower_sid,
            });
        },
    },
    redirectTo: (trans) => {
        console.log("redirecting to safety meeting base state");
        return trans
            .injector()
            .getAsync("safetyMeetingRequests")
            .then(function (safetyMeetingRequests) {
                console.log({ safetyMeetingRequests });
                let state;
                let params = {};
                let safetyMeetingRequestsPrioritzied = _.orderBy(
                    safetyMeetingRequests,
                    ["created"],
                    ["desc"],
                );
                if (safetyMeetingRequestsPrioritzied.length) {
                    state = safetyMeetingBaseState;
                    params.meeting_sid = _.first(
                        safetyMeetingRequestsPrioritzied,
                    ).safety_meeting;
                }
                let toState = trans.to();
                if (toState.name === "app.safety.grower") {
                    let $state = trans.injector().get("$state");
                    return $state.target(state, params, { reload: toState });
                } else {
                    return;
                }
            });
    },
};

export const manageMeetingCrewBossState = {
    name: "app.safety.manageMeetingCrewBoss",
    url: "/manage-meeting/{meeting_sid}",
    component: "manageMeetingCrewBoss",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "lazy.safety" */ "./lazy/safety"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        meeting: function (SafetyMeetingService, $transition$) {
            "ngInject";
            return SafetyMeetingService.retrieve(
                $transition$.params().meeting_sid,
            );
        },
        topics: function (SafetyMeetingTopicService, meeting) {
            "ngInject";
            return SafetyMeetingTopicService.several(meeting.topics);
        },
        translateds: function (
            SafetyMeetingTopicTRANSLATEDService,
            topics,
            $translate,
        ) {
            "ngInject";
            return SafetyMeetingTopicTRANSLATEDService.list({
                safety_meeting_topic__sid__in: _.join(
                    _.map(topics, SID_LOOKUP),
                ),
                language: $translate.use(),
            });
        },
        grower: function (GrowerOrganizationService, meeting) {
            "ngInject";
            return GrowerOrganizationService.retrieve(meeting.organizer);
        },
        farm: function (FarmService, meeting) {
            "ngInject";
            return FarmService.retrieve(meeting.farm);
        },
        requests: function (SafetyMeetingRequestService, $transition$) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                safety_meeting__sid: $transition$.params().meeting_sid,
            });
        },
        participants: function (requests, AccountService) {
            "ngInject";
            return AccountService.several(_.map(requests, "subject"));
        },
        crews: function (GroupService, meeting) {
            "ngInject";
            return GroupService.several(meeting.crews);
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        signatures: function (SignatureService, requests) {
            "ngInject";
            return SignatureService.several(_.map(requests, "signature"));
        },
        requestStatuses: function (
            requests,
            participants,
            growerMemberships,
            signatures,
        ) {
            "ngInject";
            return _.map(participants, function (participant) {
                let participantsRequests = _.filter(requests, [
                    "subject",
                    participant.sid,
                ]);
                for (let request of participantsRequests) {
                    request.signature = _.find(signatures, [
                        SID_LOOKUP,
                        request.signature,
                    ]);
                }
                return {
                    requests: participantsRequests,
                    participant: participant,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        participant.sid,
                    ),
                    pending: _.some(participantsRequests, [
                        "status",
                        SAFETY_PENDING,
                    ]),
                    complete: _.every(participantsRequests, [
                        "status",
                        COMPLETE,
                    ]),
                    cancelled: _.every(participantsRequests, [
                        "status",
                        CANCELLED,
                    ]),
                };
            });
        },
        completed: function (requestStatuses) {
            "ngInject";
            return _.filter(requestStatuses, "complete");
        },
    },
    data: {
        title: "routes.MANAGE_MEETING_CREW_BOSS_TITLE",
        mixpanelEvent: "Viewed manage meeting crew boss",
    },
};
export const manageMeetingCrewBossCreateNoteState = {
    name: "app.safety.manageMeetingCrewBoss.createNote",
    url: "/add-note",
    component: "manageMeetingCrewBossCreateNote",
    data: {
        title: "routes.MANAGE_MEETING_CREW_BOSS_CREATE_NOTE_TITLE",
        mixpanelEvent: "Viewed manage meeting crew boss create note",
    },
};
export const safetyMeetingScanAttendanceCrewBossState = {
    name: "app.safety.manageMeetingCrewBoss.scanAttendance",
    url: "/scan-attendance",
    component: "scanAttendance",
    resolve: {
        redirectState: function () {
            "ngInject";
            return manageMeetingCrewBossState;
        },
        manualState: function () {
            "ngInject";
            return safetyMeetingManualAttendanceCrewBossState;
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_SCAN_ATTENDANCE_CREW_BOSS_TITLE",
        mixpanelEvent: "Viewed scan attendance to safety meeting crew boss",
    },
};
export const safetyMeetingManualAttendanceCrewBossState = {
    name: "app.safety.manageMeetingCrewBoss.manualAttendance",
    url: "/manual-attendance",
    component: "manualAttendance",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        employees: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.map(employees, function (user) {
                return {
                    user: user,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        user.sid,
                    ),
                };
            });
        },
        redirectState: function () {
            "ngInject";
            return manageMeetingCrewBossState;
        },
        searchState: function () {
            "ngInject";
            return safetyMeetingScanAttendanceCrewBossState;
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_MANUAL_ATTENDANCE_CREW_BOSS_TITLE",
        mixpanelEvent: "Viewed safety meeting manual attendance crew boss",
    },
};
export const safetyMeetingBaseState = {
    name: "app.safety.meeting",
    url: "/safety-meetings/{meeting_sid}",
    resolve: {
        meeting: function (SafetyMeetingService, $transition$) {
            "ngInject";
            return SafetyMeetingService.retrieve(
                $transition$.params().meeting_sid,
            );
        },
        topics: function (SafetyMeetingTopicService, meeting) {
            "ngInject";
            return SafetyMeetingTopicService.several(meeting.topics);
        },
        requests: function (
            SafetyMeetingRequestService,
            $transition$,
            account,
        ) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                safety_meeting__sid: $transition$.params().meeting_sid,
                subject__sid: account.sid,
            });
        },
        redirect: function (meeting, requests, topics) {
            "ngInject";
            let pendingRequests = _.filter(requests, ["status", PENDING]);
            let state;
            let params;

            let pendingTrainings = _.map(pendingRequests, function (request) {
                return {
                    request,
                    topic: _.find(topics, [SID_LOOKUP, request.topic]),
                };
            });
            pendingTrainings = _.orderBy(pendingTrainings, "topic.runtime");
            if (pendingTrainings.length) {
                state = attendSafetyMeetingRequestState;
                params = {
                    meeting_sid: meeting.sid,
                    request_sid: _.first(pendingTrainings).request.sid,
                };
            } else {
                state = safetyMeetingDoneState;
                params = {
                    meeting_sid: meeting.sid,
                };
            }
            return { state: state, params: params };
        },
    },
    redirectTo: (trans) => {
        return trans.injector().getAsync("redirect");
    },
};

export const attendSafetyMeetingRequestState = {
    name: "app.safety.meeting.request",
    url: "/{request_sid}",
    component: "viewSafetyMeeting",
    resolve: {
        request: function (
            $transition$,
            SafetyMeetingRequestService,
            $state,
            $q,
        ) {
            "ngInject";
            return SafetyMeetingRequestService.retrieve(
                $transition$.params().request_sid,
            ).then(function (request) {
                if (request.status !== PENDING) {
                    let state = safetyMeetingBaseState;
                    let params = {
                        meeting_sid: $transition$.params().meeting_sid,
                    };
                    let response = $q.defer();
                    response.reject({ redirectTo: { state, params } });
                    return response.promise;
                } else {
                    return request;
                }
            });
        },
        topic: function (SafetyMeetingTopicService, request) {
            "ngInject";
            return SafetyMeetingTopicService.retrieve(request.topic);
        },
        content: function (
            SafetyMeetingTopicTRANSLATEDService,
            request,
            $translate,
        ) {
            "ngInject";
            return SafetyMeetingTopicTRANSLATEDService.first({
                safety_meeting_topic__sid: request.topic,
                language: $translate.use(),
            });
        },
        wpsTrainer: function (AccountService, meeting) {
            "ngInject";
            return AccountService.retrieve(meeting.wps_trainer);
        },
    },
    data: {
        title: "routes.ATTEND_SAFETY_MEETING_REQUEST_TITLE",
        mixpanelEvent: "Viewed safety meeting",
        mixpanelEventProperties: {
            topic__runtime: "topic.runtime",
            topic__category: "topic.category",
            topic__requires_signature: "topic.requires_signature",
            mp_trace_id: "meeting.sid",
            company_sid: "meeting.organizer",
        },
    },
};

// Deprecated
export const markAttendanceState = {
    name: "app.safety.meeting.markAttendance",
    url: "/attendance/{participant_sid}",
    component: "markAttendance",
    resolve: {
        requests: function (SafetyMeetingRequestService, $transition$) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                safety_meeting__sid: $transition$.params().meeting_sid,
                status: PENDING,
                subject__sid: $transition$.params().participant_sid,
            });
        },
        topics: function (SafetyMeetingTopicService, meeting) {
            "ngInject";
            return SafetyMeetingTopicService.several(meeting.topics);
        },
    },
    data: {
        title: "routes.MARK_ATTENDANCE_TITLE",
        mixpanelEvent: "Viewed mark attendance",
    },
};

export const safetyMeetingDoneState = {
    name: "app.safety.meeting.done",
    url: "/done",
    component: "safetyMeetingDone",
    data: {
        title: "routes.SAFETY_MEETING_DONE_TITLE",
        mixpanelEvent: "Viewed post safety meeting",
        mixpanelEventProperties: {
            mp_trace_id: "meeting.sid",
            company_sid: "meeting.organizer",
        },
    },
};
export const standaloneFeedbackState = {
    name: "app.standaloneFeedback",
    url: "/feedback/{grower_sid}",
    component: "standaloneFeedback",
    resolve: {
        grower: function ($transition$, GrowerOrganizationService) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
    },
    data: {
        title: "routes.STANDALONE_FEEDBACK",
        mixpanelEvent: "Viewed standalone feedback",
        mixpanelEventProperties: {
            company_sid: "grower.sid",
        },
    },
};
export const announcementsState = {
    name: "app.announcements",
    url: "/announcements",
    abstract: true,
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(
                /* webpackChunkName: "common.communication" */ "./common/communication"
            ),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    data: {},
};
export const announcementState = {
    name: "app.announcements.announcement",
    url: "/{announcement_sid}",
    component: "viewMessage",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
                {},
                {
                    expand: ["translations", "farm"],
                },
            );
        },
        translated: function (announcement, $translate) {
            "ngInject";
            return _.find(announcement.translations, [
                "language",
                $translate.use(),
            ]);
        },
        attachments: function (AttachmentService, $transition$) {
            "ngInject";
            return AttachmentService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
    },
    data: {
        title: "routes.ANNOUNCEMENT_TITLE",
        mixpanelEvent: "Viewed message",
    },
};
export const announcementTranslatedState = {
    name: "app.announcements.announcement.translated",
    url: "/translations/{translated_sid}",
    abstract: true,
    resolve: {
        translated: function ($transition$, announcement) {
            "ngInject";
            return _.find(announcement.translations, [
                SID_LOOKUP,
                $transition$.params().translated_sid,
            ]);
        },
    },
};
export const announcementAttachmentsState = {
    name: "app.announcements.announcement.attachments",
    url: "/attachments",
    abstract: true,
};
export const announcementAttachmentState = {
    name: "app.announcements.announcement.attachments.attachment",
    url: "/{attachment_sid}",
    external: true,
    resolve: {
        attachment: function (attachments, $transition$) {
            "ngInject";
            return _.find(attachments, [
                SID_LOOKUP,
                $transition$.params().attachment_sid,
            ]);
        },
        redirect: function (attachment) {
            "ngInject";
            if (attachment.external_file_id) {
                return getCloudinaryUrl(attachment.external_file_id);
            } else {
                return attachment.file;
            }
        },
    },
    data: {
        title: "routes.ANNOUNCEMENT_ATTACHMENT_TITLE",
        mixpanelEvent: "Viewed attachment",
    },
};
export const announcementAudioState = {
    name: "app.announcements.announcement.translated.audio",
    url: "/audio",
    external: true,
    resolve: {
        redirect: function (translated) {
            "ngInject";
            return translated.audio_file;
        },
    },
    data: {
        title: "routes.ANNOUNCEMENT_AUDIO_TITLE",
        mixpanelEvent: "Viewed announcement audio",
    },
};
export const paperworkState = {
    name: "app.paperwork",
    url: "/paperwork",
    abstract: true,
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "lazy.paperwork" */ "./lazy/paperwork"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    data: {},
};

export const growerPaperworkState = {
    name: "app.paperwork.grower",
    url: "/{grower_sid}?skip__document_package_request__sid",
    params: {
        skip__document_package_request__sid: {
            inherit: false,
            value: undefined,
        },
    },
    component: "growerPaperwork",
    resolve: {
        grower: function ($transition$, GrowerOrganizationService) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
        growerMembership: function (
            $transition$,
            GrowerMembershipService,
            account,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: account.sid,
            });
        },
        myIncompleteDocumentRequests: function (
            DocumentPackageRequestService,
            growerMembership,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                grower_membership__sid: growerMembership.sid,
                completed: false,
                cancelled: false,
                requester__sid: $transition$.params().grower_sid,
            }).then(function (requests) {
                console.log("Before");
                console.log(requests);
                let x = _.filter(requests, function (request) {
                    return (
                        request.sid !==
                        $transition$.params()
                            .skip__document_package_request__sid
                    );
                });
                console.log("After");
                console.log(x);
                return x;
            });
        },
        mySignatureRequests: function (
            SignatureRequestService,
            growerMembership,
            myIncompleteDocumentRequests,
        ) {
            "ngInject";
            return SignatureRequestService.list({
                grower_membership__sid: growerMembership.sid,
                role: SIGNER,
                document_package_request__sid__in: _.join(
                    _.map(myIncompleteDocumentRequests, SID_LOOKUP),
                ),
            });
        },
        documentPackages: function (
            myIncompleteDocumentRequests,
            DocumentPackageService,
        ) {
            "ngInject";
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    _.map(myIncompleteDocumentRequests, SID_LOOKUP),
                ),
            });
        },
        paperworkToDo: function (
            myIncompleteDocumentRequests,
            mySignatureRequests,
            documentPackages,
        ) {
            "ngInject";
            return _.map(
                myIncompleteDocumentRequests,
                function (documentRequest) {
                    let documentPackage = _.find(documentPackages, [
                        SID_LOOKUP,
                        documentRequest.document_package,
                    ]);
                    return {
                        documentRequest: documentRequest,
                        signatureRequest: _.find(mySignatureRequests, [
                            "document_package_request",
                            documentRequest.sid,
                        ]),
                        documentPackage,
                        nextRole: getNextRole(documentRequest, documentPackage),
                    };
                },
            );
        },
        newPaperworkWaitingForPrefill: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return paperwork.documentRequest.isInPrefill();
            });
        },
        oldPaperworkToStart: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return (
                    paperwork.nextRole === SIGNER &&
                    _.isNil(
                        paperwork.documentRequest
                            .next_pending_signature_request,
                    ) &&
                    _.isNil(paperwork.signatureRequest) &&
                    !paperwork.documentRequest.isInPrefill() &&
                    !paperwork.documentRequest.source
                );
            });
        },
        paperworkV3ToSign: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return (
                    _.isNil(
                        _.get(paperwork.signatureRequest, "data_request_id"),
                    ) &&
                    _.get(paperwork.signatureRequest, "sid") ===
                        paperwork.documentRequest.next_pending_signature_request
                );
            });
        },
        redirectData: function (
            oldPaperworkToStart,
            newPaperworkWaitingForPrefill,
            paperworkV3ToSign,
        ) {
            "ngInject";
            return [
                oldPaperworkToStart,
                newPaperworkWaitingForPrefill,
                paperworkV3ToSign,
            ];
        },
    },
    redirectTo: (trans) => {
        return trans
            .injector()
            .getAsync("redirectData")
            .then(function ([
                oldPaperworkToStart,
                newPaperworkWaitingForPrefill,
                paperworkV3ToSign,
            ]) {
                console.log({ oldPaperworkToStart });
                console.log({ newPaperworkWaitingForPrefill });
                console.log({ paperworkV3ToSign });
                let state;
                let params = {
                    grower_sid: trans.params().grower_sid,
                };
                if (oldPaperworkToStart.length) {
                    state = oldStartState;
                    params.document_request_sid =
                        _.first(oldPaperworkToStart).documentRequest.sid;
                } else if (paperworkV3ToSign.length) {
                    state = v3SignState;
                } else if (newPaperworkWaitingForPrefill.length) {
                    state = growerPaperworkWaitingState;
                } else {
                    state = growerPaperworkDoneState;
                }
                let toState = trans.to();
                if (toState.name === "app.paperwork.grower") {
                    let $state = trans.injector().get("$state");
                    return $state.target(state, params, { reload: toState });
                } else {
                    return;
                }
            });
    },
};

export const growerPaperworkWaitingState = {
    name: "app.paperwork.grower.waiting",
    url: "/waiting",
    component: "growerPaperworkWaiting",
    resolve: {
        redirectState: function () {
            "ngInject";
            return growerPaperworkState;
        },
    },
    data: {
        title: "routes.GROWER_PAPERWORK_WAITING_TITLE",
        mixpanelEvent: "Viewed paperwork waiting",
    },
};
export const oldStartState = {
    name: "app.paperwork.grower.oldStart",
    url: "/old-start/{document_request_sid}",
    component: "startPaperwork",
    resolve: {
        documentPackageRequest: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.retrieve(
                $transition$.params().document_request_sid,
            );
        },
        documentPackage: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.first({
                documentpackagerequest__sid:
                    $transition$.params().document_request_sid,
            });
        },
        documentPackageCustomizationTranslated: function (
            DocumentPackageCustomizationTranslatedService,
            documentPackageRequest,
            $translate,
        ) {
            "ngInject";
            if (documentPackageRequest.document_package_customization) {
                return DocumentPackageCustomizationTranslatedService.first({
                    document_package_customization__sid:
                        documentPackageRequest.document_package_customization,
                    language: $translate.use(),
                });
            }
        },
        prefillData: function (DocumentPackageRequestService, $transition$) {
            "ngInject";
            return DocumentPackageRequestService.prefillRequest(
                $transition$.params().document_request_sid,
            );
        },
        formData: function (prefillData, formSchema) {
            "ngInject";
            return filterOutBadEnums(prefillData, formSchema);
        },
        formSchema: function (documentPackage, documentPackageRequest) {
            "ngInject";
            addBreadcrumb({
                category: "resolve",
                message: "documentPackage",
                data: documentPackage,
            });
            let signatureFieldNames = getSignatureFieldNames(documentPackage);
            let signerFields = _.get(documentPackage.fields, SIGNER, []);
            // if there is a customization remove requester fields
            if (documentPackageRequest.document_package_customization) {
                signerFields = _.difference(
                    signerFields,
                    documentPackage.subject_fields.requester,
                );
            }
            return documentPackage.getFormSchemaRaw().then((data) => {
                let schema = data.schema;
                let fieldsToRemove = _.difference(
                    _.keys(schema.properties),
                    signerFields,
                );
                let fieldsThatPeopleCouldFillOut = _.flatten(
                    _.values(documentPackage.fields),
                );
                let fieldsToKeep = _.pick(
                    schema.properties,
                    fieldsThatPeopleCouldFillOut,
                );
                schema.properties = _.omit(
                    fieldsToKeep,
                    _.concat(fieldsToRemove, signatureFieldNames),
                );

                // Remove required fields
                if (_.has(schema, "required")) {
                    let required = _.intersection(
                        schema.required,
                        signerFields,
                    );
                    schema.required = _.difference(
                        required,
                        signatureFieldNames,
                    );
                }
                return schema;
            });
        },
        redirectState: function () {
            "ngInject";
            return growerPaperworkState;
        },
        role: function () {
            "ngInject";
            return SIGNER;
        },
        autoSubmit: function (documentPackageRequest) {
            "ngInject";
            return documentPackageRequest.initiator_fill_first;
        },
    },
    data: {
        title: "routes.START_TITLE",
        mixpanelEvent: "Signer viewed paperwork simple form",
    },
    redirectTo: (trans) => {
        let requestResolve = trans
            .injector()
            .getAsync("documentPackageRequest");
        let accountResolve = trans.injector().getAsync("account");
        let redirectStateResolve = trans.injector().getAsync("redirectState");
        let $transition$ = trans.injector().get("$transition$");
        let $q = trans.injector().get("$q");
        return $q
            .all([requestResolve, accountResolve, redirectStateResolve])
            .then(function (resolves) {
                let [request, account, redirectState] = resolves;
                let targetRedirectState =
                    $transition$.router.stateService.target(
                        redirectState,
                        { grower_sid: request.requester },
                        { reload: redirectState },
                    );
                if (request.completed || request.cancelled) {
                    return targetRedirectState;
                } else if (request.next_pending_signature_request) {
                    let SignatureRequestService = trans
                        .injector()
                        .get("SignatureRequestService");
                    return SignatureRequestService.retrieve(
                        request.next_pending_signature_request,
                    ).then(function (signatureRequest) {
                        if (signatureRequest.signer === account.sid) {
                            return targetRedirectState;
                        }
                    });
                }
            });
    },
};
export const v3SignState = {
    name: "app.paperwork.grower.v3Sign",
    url: "/v3-sign",
    component: "newSignPaperwork",
    resolve: {
        documentPackageRequests: function (
            DocumentPackageRequestService,
            $transition$,
            paperworkV3ToSign,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                completed: false,
                cancelled: false,
                sid__in: _.join(
                    _.map(paperworkV3ToSign, "documentRequest.sid"),
                ),
            });
        },
        documentPackages: function (
            DocumentPackageService,
            $transition$,
            paperworkV3ToSign,
        ) {
            "ngInject";
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    _.map(paperworkV3ToSign, "documentRequest.sid"),
                ),
            });
        },
        signers: function (AccountService, documentPackageRequests) {
            "ngInject";
            return AccountService.several(
                _.uniq(_.map(documentPackageRequests, "signer")),
            );
        },
        signatureRequests: function (
            $transition$,
            SignatureRequestService,
            accountSid,
            paperworkV3ToSign,
        ) {
            "ngInject";
            return SignatureRequestService.list({
                signer__sid: accountSid,
                status: PENDING,
                role: SIGNER,
                document_package_request__sid__in: _.join(
                    _.map(paperworkV3ToSign, "documentRequest.sid"),
                ),
            });
        },
        redirectState: function () {
            "ngInject";
            return growerPaperworkState;
        },
        reloadState: function () {
            "ngInject";
            return growerPaperworkState;
        },
        initialSignature: function (
            PaperworkSignatureService,
            accountSid,
            $transition$,
        ) {
            "ngInject";
            return PaperworkSignatureService.get(
                accountSid,
                $transition$.params().grower_sid,
            );
        },
        requests: function (
            signatureRequests,
            documentPackages,
            documentPackageRequests,
        ) {
            "ngInject";
            return _.map(signatureRequests, function (signatureRequest) {
                return {
                    signatureRequest,
                    documentPackageRequest: _.find(documentPackageRequests, [
                        SID_LOOKUP,
                        signatureRequest.document_package_request,
                    ]),
                    documentPackage: _.find(documentPackages, [
                        SID_LOOKUP,
                        signatureRequest.document_package,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.V3_SIGN_TITLE",
        mixpanelEvent: "Signer viewed paperwork for signature",
    },
    redirectTo: (trans) => {
        return trans
            .injector()
            .getAsync("documentPackageRequests")
            .then(function (requests) {
                if (requests.length === 0) {
                    return trans.router.stateService.target(
                        growerPaperworkState,
                        trans.params(),
                    );
                }
            });
    },
};
export const growerPaperworkDoneState = {
    name: "app.paperwork.grower.done",
    url: "/done",
    component: "growerPaperworkDone",
    resolve: {
        redirectState: function () {
            "ngInject";
            return growerPaperworkState;
        },
    },
    data: {
        title: "routes.GROWER_PAPERWORK_DONE_TITLE",
        mixpanelEvent: "Viewed paperwork done",
    },
};
export const documentScanStartState = {
    name: "app.documentScanStart",
    url: "/document-scan?user_sid?grower_sid?grower_membership_sid",
    params: {
        user_sid: {
            dynamic: true,
        },
        grower_sid: {
            dynamic: true,
        },
        grower_membership_sid: {
            dynamic: true,
        },
    },
    component: "documentScanStart",
    resolve: {
        userSid: function ($transition$) {
            "ngInject";
            return $transition$.params().user_sid;
        },
        growerSid: function ($transition$) {
            "ngInject";
            return $transition$.params().grower_sid;
        },
        growerMembershipSid: function ($transition$) {
            "ngInject";
            return $transition$.params().grower_membership_sid;
        },
        growerMembership: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                $transition$.params().grower_membership_sid,
            );
        },
    },
    data: {
        title: "routes.DOCUMENT_SCAN_START",
        mixpanelEvent: "Viewed document scan start",
    },
};
export const handbooksState = {
    name: "app.handbooks",
    url: "/handbooks",
    abstract: true,
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "lazy.handbooks" */ "./lazy/handbooks"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    data: {},
};
export const growerHandbooksState = {
    name: "app.handbooks.grower",
    url: "/{grower_sid}",
    component: "viewHandbooks",
    resolve: {
        grower: function ($transition$, GrowerOrganizationService) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
        handbooks: function ($transition$, DocumentPackageService) {
            "ngInject";
            return DocumentPackageService.list({
                active: true,
                category: "handbook",
                owner__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.VIEW_HANDBOOKS_TITLE",
        mixpanelEvent: "Viewed handbook",
        breadcrumb: "crumbs.VIEW_HANDBOOKS",
    },
};
export const myDocumentsState = {
    name: "app.myDocuments",
    url: "/my-documents/{grower_sid}",
    component: "myDocuments",
    resolve: {
        grower: function ($transition$, GrowerOrganizationService) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
        myGrowerMembership: function (
            GrowerMembershipService,
            account,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: account.sid,
            });
        },
        completedPaperworkRecords: function (
            DocumentPackageRecordService,
            $transition$,
            myGrowerMembership,
        ) {
            "ngInject";
            return DocumentPackageRecordService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                grower_membership__sid: myGrowerMembership.sid,
                completion_date__isnull: false,
                archived: false,
            });
        },
        documentPackages: function (
            DocumentPackageService,
            completedPaperworkRecords,
        ) {
            "ngInject";
            let recordPackageSids = _.map(
                completedPaperworkRecords,
                "document_package",
            );
            let packageSids = _.uniq(recordPackageSids);
            return DocumentPackageService.several(packageSids);
        },
        paperworks: function (completedPaperworkRecords, documentPackages) {
            "ngInject";
            return _.map(completedPaperworkRecords, function (record) {
                return {
                    record: record,
                    documentPackage: _.find(documentPackages, [
                        SID_LOOKUP,
                        record.document_package,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.VIEW_MY_DOCUMENTS_TITLE",
        mixpanelEvent: "Viewed my documents",
    },
};
export const crewCreateDisciplineReportState = {
    name: "app.createDisciplineReport",
    url: "/manager/{grower_sid}/record-employee-issue/create",
    component: "addDescriptionDisciplineReport",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(
                /* webpackChunkName: "lazy.discipline" */ "./lazy/discipline"
            ),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        grower: function ($transition$, GrowerOrganizationService) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.CREATE_DISCIPLINE_REPORT_TITLE",
        mixpanelEvent: "Viewed crew create discipline report",
    },
};
export const externalPaperworkState = {
    name: "app.paperwork.external",
    url: "/external/{grower_sid}",
    component: "growerPaperwork",
    resolve: {
        grower: function ($transition$, GrowerOrganizationService) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
        myIncompleteDocumentRequests: function (
            DocumentPackageRequestService,
            account,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                completed: false,
                cancelled: false,
                requester__sid: $transition$.params().grower_sid,
                external_signer__sid: account.sid,
            });
        },
        mySignatureRequests: function (
            SignatureRequestService,
            accountSid,
            $transition$,
        ) {
            "ngInject";
            let params = {
                signer__sid: accountSid,
                role: EXTERNAL,
                document_package_request__requester__sid:
                    $transition$.params().grower_sid,
                status: PAPERWORK_PENDING,
            };
            return SignatureRequestService.list(params);
        },
        documentPackages: function (
            myIncompleteDocumentRequests,
            DocumentPackageService,
        ) {
            "ngInject";
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    _.map(myIncompleteDocumentRequests, SID_LOOKUP),
                ),
            });
        },
        paperworkToDo: function (
            myIncompleteDocumentRequests,
            mySignatureRequests,
            documentPackages,
        ) {
            "ngInject";
            return _.map(
                myIncompleteDocumentRequests,
                function (documentRequest) {
                    let documentPackage = _.find(documentPackages, [
                        SID_LOOKUP,
                        documentRequest.document_package,
                    ]);
                    return {
                        documentRequest: documentRequest,
                        signatureRequest: _.find(mySignatureRequests, [
                            "document_package_request",
                            documentRequest.sid,
                        ]),
                        documentPackage,
                        nextRole: getNextRole(documentRequest, documentPackage),
                    };
                },
            );
        },
        newPaperworkWaitingForPrefill: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return paperwork.documentRequest.isInPrefill();
            });
        },
        oldPaperworkToStart: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return (
                    paperwork.nextRole === EXTERNAL &&
                    _.isNil(
                        paperwork.documentRequest
                            .next_pending_signature_request,
                    ) &&
                    _.isNil(paperwork.signatureRequest) &&
                    !paperwork.documentRequest.isInPrefill() &&
                    !paperwork.documentRequest.source
                );
            });
        },
        paperworkV3ToSign: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return (
                    _.isNil(
                        _.get(paperwork.signatureRequest, "data_request_id"),
                    ) &&
                    _.get(paperwork.signatureRequest, "sid") ===
                        paperwork.documentRequest.next_pending_signature_request
                );
            });
        },
        redirectData: function (
            oldPaperworkToStart,
            newPaperworkWaitingForPrefill,
            paperworkV3ToSign,
        ) {
            "ngInject";
            return [
                oldPaperworkToStart,
                newPaperworkWaitingForPrefill,
                paperworkV3ToSign,
            ];
        },
    },
    redirectTo: (trans) => {
        return trans
            .injector()
            .getAsync("redirectData")
            .then(function ([
                oldPaperworkToStart,
                newPaperworkWaitingForPrefill,
                paperworkV3ToSign,
            ]) {
                console.log({ oldPaperworkToStart });
                console.log({ newPaperworkWaitingForPrefill });
                console.log({ paperworkV3ToSign });
                let state;
                let params = {
                    grower_sid: trans.params().grower_sid,
                };
                if (oldPaperworkToStart.length) {
                    state = externalPaperworkStartState;
                    params.document_request_sid =
                        _.first(oldPaperworkToStart).documentRequest.sid;
                } else if (paperworkV3ToSign.length) {
                    state = externalv3SignState;
                } else {
                    state = externalPaperworkDoneState;
                }
                let toState = trans.to();
                if (toState.name === "app.paperwork.external") {
                    let $state = trans.injector().get("$state");
                    return $state.target(state, params, { reload: toState });
                } else {
                    return;
                }
            });
    },
};
export const externalPaperworkStartState = {
    name: "app.paperwork.external.start",
    url: "/start/{document_request_sid}",
    component: "startPaperwork",
    resolve: {
        documentPackageRequest: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.retrieve(
                $transition$.params().document_request_sid,
            );
        },
        documentPackage: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.first({
                documentpackagerequest__sid:
                    $transition$.params().document_request_sid,
            });
        },
        prefillData: function (DocumentPackageRequestService, $transition$) {
            "ngInject";
            return {};
        },
        formData: function (prefillData) {
            "ngInject";
            return prefillData;
        },
        formSchema: function (documentPackage, documentPackageRequest) {
            "ngInject";
            addBreadcrumb({
                category: "resolve",
                message: "documentPackage",
                data: documentPackage,
            });
            let signatureFieldNames = getSignatureFieldNames(documentPackage);
            let signerFields = _.get(documentPackage.fields, EXTERNAL, []);
            // if there is a customization remove requester fields
            if (documentPackageRequest.document_package_customization) {
                signerFields = _.difference(
                    signerFields,
                    documentPackage.subject_fields.requester,
                );
            }
            return documentPackage.getFormSchemaRaw().then((data) => {
                let schema = data.schema;
                let fieldsToRemove = _.difference(
                    _.keys(schema.properties),
                    signerFields,
                );
                let fieldsThatPeopleCouldFillOut = _.flatten(
                    _.values(documentPackage.fields),
                );
                let fieldsToKeep = _.pick(
                    schema.properties,
                    fieldsThatPeopleCouldFillOut,
                );
                schema.properties = _.omit(
                    fieldsToKeep,
                    _.concat(fieldsToRemove, signatureFieldNames),
                );

                // Remove required fields
                if (_.has(schema, "required")) {
                    let required = _.intersection(
                        schema.required,
                        signerFields,
                    );
                    schema.required = _.difference(
                        required,
                        signatureFieldNames,
                    );
                }
                return schema;
            });
        },
        redirectState: function () {
            "ngInject";
            return externalPaperworkState;
        },
        role: function () {
            "ngInject";
            return EXTERNAL;
        },
        autoSubmit: function () {
            "ngInject";
            return false;
        },
    },
    data: {
        title: "routes.START_TITLE",
        mixpanelEvent: "Signer viewed paperwork simple form",
    },
    redirectTo: (trans) => {
        let requestResolve = trans
            .injector()
            .getAsync("documentPackageRequest");
        let accountResolve = trans.injector().getAsync("account");
        let redirectStateResolve = trans.injector().getAsync("redirectState");
        let $transition$ = trans.injector().get("$transition$");
        let $q = trans.injector().get("$q");
        return $q
            .all([requestResolve, accountResolve, redirectStateResolve])
            .then(function (resolves) {
                let [request, account, redirectState] = resolves;
                let targetRedirectState =
                    $transition$.router.stateService.target(
                        redirectState,
                        { grower_sid: request.requester },
                        { reload: redirectState },
                    );
                if (request.completed || request.cancelled) {
                    return targetRedirectState;
                } else if (request.next_pending_signature_request) {
                    let SignatureRequestService = trans
                        .injector()
                        .get("SignatureRequestService");
                    return SignatureRequestService.retrieve(
                        request.next_pending_signature_request,
                    ).then(function (signatureRequest) {
                        if (signatureRequest.signer === account.sid) {
                            return targetRedirectState;
                        }
                    });
                }
            });
    },
};
export const externalv3SignState = {
    name: "app.paperwork.external.v3Sign",
    url: "/v3-sign",
    component: "newSignPaperwork",
    resolve: {
        documentPackageRequests: function (
            DocumentPackageRequestService,
            $transition$,
            paperworkV3ToSign,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                completed: false,
                cancelled: false,
                sid__in: _.join(
                    _.map(paperworkV3ToSign, "documentRequest.sid"),
                ),
            });
        },
        documentPackages: function (
            DocumentPackageService,
            $transition$,
            paperworkV3ToSign,
        ) {
            "ngInject";
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    _.map(paperworkV3ToSign, "documentRequest.sid"),
                ),
            });
        },
        signers: function (AccountService, documentPackageRequests) {
            "ngInject";
            return AccountService.several(
                _.uniq(_.map(documentPackageRequests, "signer")),
            );
        },
        signatureRequests: function (
            $transition$,
            SignatureRequestService,
            accountSid,
            paperworkV3ToSign,
        ) {
            "ngInject";
            return SignatureRequestService.list({
                signer__sid: accountSid,
                status: PENDING,
                role: EXTERNAL,
                document_package_request__sid__in: _.join(
                    _.map(paperworkV3ToSign, "documentRequest.sid"),
                ),
            });
        },
        redirectState: function () {
            "ngInject";
            return externalPaperworkState;
        },
        reloadState: function () {
            "ngInject";
            return externalPaperworkState;
        },
        requests: function (
            signatureRequests,
            documentPackages,
            documentPackageRequests,
        ) {
            "ngInject";
            return _.map(signatureRequests, function (signatureRequest) {
                return {
                    signatureRequest,
                    documentPackageRequest: _.find(documentPackageRequests, [
                        SID_LOOKUP,
                        signatureRequest.document_package_request,
                    ]),
                    documentPackage: _.find(documentPackages, [
                        SID_LOOKUP,
                        signatureRequest.document_package,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.V3_SIGN_TITLE",
        mixpanelEvent: "Signer viewed paperwork for signature",
    },
    redirectTo: (trans) => {
        return trans
            .injector()
            .getAsync("documentPackageRequests")
            .then(function (requests) {
                if (requests.length === 0) {
                    return trans.router.stateService.target(
                        externalPaperworkState,
                        trans.params(),
                    );
                }
            });
    },
};
export const externalPaperworkDoneState = {
    name: "app.paperwork.external.done",
    url: "/done",
    component: "growerPaperworkDone",
    resolve: {
        redirectState: function () {
            "ngInject";
            return externalPaperworkState;
        },
    },
    data: {
        title: "routes.GROWER_PAPERWORK_DONE_TITLE",
        mixpanelEvent: "Viewed external paperwork done",
    },
};
export const sopLibraryBaseState = {
    name: "app.sopLibrary",
    url: "/sop-library",
    abstract: true,
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "common.safety" */ "./common/safety"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    data: {},
};
export const sopLibraryState = {
    name: "app.sopLibrary.grower",
    url: "/{grower_sid}",
    component: "sopLibrary",
    resolve: {
        topics: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.list({
                active: true,
                owner__sid: $transition$.params().grower_sid,
                available_in_sop_library: true,
            });
        },
        grower: function (GrowerOrganizationService, $transition$) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
    },
    data: {
        title: "routes.SOP_LIBRARY_TITLE",
        mixpanelEvent: "Viewed SOP library",
    },
};
export const viewSopState = {
    name: "app.sopLibrary.grower.view",
    url: "/{topic_sid}",
    component: "viewSop",
    resolve: {
        topic: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.retrieve(
                $transition$.params().topic_sid,
            );
        },
        content: function (
            SafetyMeetingTopicTRANSLATEDService,
            topic,
            $translate,
        ) {
            "ngInject";
            return SafetyMeetingTopicTRANSLATEDService.first({
                safety_meeting_topic__sid: topic.sid,
                language: $translate.use(),
            });
        },
    },
    data: {
        title: "routes.VIEW_SOP_TITLE",
        mixpanelEvent: "Viewed SOP topic",
        mixpanelEventProperties: {
            topic__runtime: "topic.runtime",
            topic__category: "topic.category",
            topic__requires_signature: "topic.requires_signature",
            company_sid: "grower.sid",
        },
    },
};
